import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/brand";
import { Lifebuoy, PaperPlaneTilt, ChatCircleDots, Question } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function HelpPage() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general");
  const [busy, setBusy] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [faqCategories, setFaqCategories] = useState([]);
  const [loadingFaqs, setLoadingFaqs] = useState(true);

  const loadFaqs = async () => {
    setLoadingFaqs(true);
    try {
      const res = await axios.get(`${API}/help/faqs`);
      setFaqs(res.data.faqs || []);
      setFaqCategories(res.data.categories || []);
    } catch (e) {
      /* silent */
    } finally {
      setLoadingFaqs(false);
    }
  };

  const loadTickets = async () => {
    try {
      const res = await axios.get(`${API}/support/tickets/mine`);
      setTickets(res.data.tickets || []);
    } catch (e) {
      /* silent */
    }
  };

  useEffect(() => {
    loadFaqs();
    loadTickets();
  }, []);

  // Group FAQs under their category. Preserve category `sort_order`; put
  // uncategorised items last.
  const groupedFaqs = useMemo(() => {
    const byCat = {};
    for (const c of faqCategories) byCat[c.id] = { category: c, items: [] };
    const uncategorised = [];
    for (const f of faqs) {
      if (f.category_id && byCat[f.category_id]) {
        byCat[f.category_id].items.push(f);
      } else {
        uncategorised.push(f);
      }
    }
    const ordered = [...faqCategories]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((c) => byCat[c.id])
      .filter((g) => g.items.length > 0);
    if (uncategorised.length > 0) {
      ordered.push({ category: { id: "_uncat", name: "Other" }, items: uncategorised });
    }
    return ordered;
  }, [faqs, faqCategories]);

  const submit = async (e) => {
    e.preventDefault();
    if (subject.length < 3 || message.length < 10) return;
    setBusy(true);
    try {
      await axios.post(`${API}/support/tickets`, { subject, message, category });
      toast.success("Ticket submitted. We'll be back to you within 48 hours.");
      setSubject("");
      setMessage("");
      setCategory("general");
      loadTickets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit ticket");
    } finally {
      setBusy(false);
    }
  };

  const statusChip = (status) => {
    const style = {
      new: "bg-[#FFBD4A] border-black",
      in_progress: "bg-blue-100 border-blue-700 text-blue-900",
      waiting_user: "bg-purple-100 border-purple-700 text-purple-900",
      done: "bg-emerald-100 border-emerald-700 text-emerald-900",
    }[status] || "bg-black/10 border-black/30";
    return (
      <span className={`text-[10px] uppercase tracking-widest font-heading px-2 py-1 rounded border ${style}`}>
        {status.replace("_", " ")}
      </span>
    );
  };

  const TicketRow = ({ ticket: t, onChange }) => {
    const [editing, setEditing] = useState(false);
    const [busySave, setBusySave] = useState(false);
    const [draft, setDraft] = useState({ subject: t.subject, message: t.message });
    const locked = t.status === "done";

    const save = async () => {
      setBusySave(true);
      try {
        await axios.patch(`${API}/support/tickets/${t.id}/mine`, draft);
        toast.success("Ticket updated");
        setEditing(false);
        onChange();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not update");
      } finally {
        setBusySave(false);
      }
    };

    const del = async () => {
      if (!window.confirm("Delete this ticket? It'll disappear from your list — you can always open a new one.")) return;
      try {
        await axios.delete(`${API}/support/tickets/${t.id}/mine`);
        toast.success("Ticket deleted");
        onChange();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not delete");
      }
    };

    return (
      <div className="py-3" data-testid={`ticket-${t.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {editing ? (
              <div className="space-y-2">
                <input
                  className="input"
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  data-testid={`ticket-edit-subject-${t.id}`}
                />
                <textarea
                  rows={3}
                  className="input"
                  value={draft.message}
                  onChange={(e) => setDraft({ ...draft, message: e.target.value })}
                  data-testid={`ticket-edit-message-${t.id}`}
                />
                <div className="flex gap-2">
                  <button type="button" className="btn-primary" onClick={save} disabled={busySave}
                          data-testid={`ticket-save-${t.id}`}>
                    {busySave ? "Saving…" : "Save"}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => { setEditing(false); setDraft({ subject: t.subject, message: t.message }); }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{t.subject}</span>
                  {statusChip(t.status)}
                </div>
                <p className="text-xs text-black/60 mt-1 whitespace-pre-wrap">{t.message}</p>
                <p className="text-[10px] text-black/45 mt-1">
                  {new Date(t.created_at).toLocaleString("en-GB")}
                </p>
                {!locked && (
                  <div className="flex gap-3 mt-2">
                    <button type="button" onClick={() => setEditing(true)}
                            className="text-xs underline text-black/70 hover:text-black"
                            data-testid={`ticket-edit-${t.id}`}>Edit</button>
                    <button type="button" onClick={del}
                            className="text-xs underline text-red-700 hover:text-red-900"
                            data-testid={`ticket-delete-${t.id}`}>Delete</button>
                  </div>
                )}
                {locked && (
                  <p className="text-[10px] text-black/45 mt-2 italic">
                    This ticket is closed. Open a new one if you need to follow up.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8" data-testid="help-page">
      <div>
        <div className="section-eyebrow flex items-center gap-2">
          <Lifebuoy size={16} weight="bold" /> Help & Support
        </div>
        <h1 className="font-heading uppercase text-4xl mt-2">We've got your back.</h1>
        <p className="text-black/75 mt-3 leading-relaxed">
          Stuck on something? You're in the right place. Skim the FAQ below for the most-asked
          questions. If the answer isn't there, submit a ticket and we'll be back in your inbox
          within 48 hours (weekdays). Every reply is personal — no bots, no scripts.
        </p>
        <p className="text-black/75 mt-3 leading-relaxed">
          And if there's something you'd love forecastly to do next?{" "}
          <Link to="/roadmap" className="font-semibold underline">
            Visit our roadmap
          </Link>{" "}
          and shout about it — top-voted ideas move to the top of the build list.
        </p>
      </div>

      {/* FAQ — grouped by category */}
      <div className="card">
        <h2 className="font-heading uppercase text-2xl flex items-center gap-2">
          <Question size={22} weight="duotone" /> Frequently asked
        </h2>
        <div className="mt-4 space-y-8">
          {loadingFaqs && faqs.length === 0 ? (
            <p className="text-sm text-black/55">Loading…</p>
          ) : groupedFaqs.length === 0 ? (
            <p className="text-sm text-black/55">No FAQs yet — check back soon.</p>
          ) : (
            groupedFaqs.map((group) => (
              <div key={group.category.id} data-testid={`faq-group-${group.category.id}`}>
                <h3 className="section-eyebrow border-b border-black/10 pb-2 mb-3">
                  {group.category.name}
                </h3>
                <div className="space-y-3">
                  {group.items.map((item, i) => (
                    <details
                      key={item.id}
                      className="border-[1.5px] border-black/15 rounded-xl p-3 hover:bg-[#FFF7E5] group"
                      data-testid={`faq-item-${item.id}`}
                    >
                      <summary className="cursor-pointer font-semibold text-sm select-none">
                        {item.question}
                      </summary>
                      <div
                        className="mt-2 text-sm text-black/75 leading-relaxed prose prose-sm max-w-none"
                        // Answers can now contain rich text (bold, italic, links) authored via the
                        // admin FAQ editor's shared RichTextEditor component. Content is admin-only
                        // authored so trusted; still keep it in a bounded prose container.
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: item.answer || "" }}
                      />
                    </details>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
        <p className="text-xs text-black/55 mt-6">
          Full help centre with videos and walkthroughs coming to{" "}
          <a
            href="https://forecastly.me/help"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline"
          >
            forecastly.me/help
          </a>{" "}
          soon.
        </p>
      </div>

      {/* Submit ticket */}
      <div className="card">
        <h2 className="font-heading uppercase text-2xl flex items-center gap-2">
          <PaperPlaneTilt size={22} weight="duotone" /> Submit a ticket
        </h2>
        <p className="mt-2 text-sm text-black/65">
          Can't find your answer? Send us a ticket and we'll reply within 48 hours (weekdays).
        </p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div className="grid sm:grid-cols-[1fr_180px] gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">Subject</label>
              <input
                type="text"
                className="input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="A short summary…"
                required
                minLength={3}
                maxLength={200}
                data-testid="ticket-subject"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Category</label>
              <select
                className="input"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="ticket-category"
              >
                <option value="general">General</option>
                <option value="bug">Bug report</option>
                <option value="billing">Billing / subscription</option>
                <option value="feature-request">Feature request</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Message</label>
            <textarea
              className="input min-h-[140px]"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what's going on. Screenshots welcome — reply to our email with any attachments."
              required
              minLength={10}
              maxLength={8000}
              data-testid="ticket-message"
            />
          </div>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="ticket-submit">
            {busy ? "Sending…" : "Send ticket"}
          </button>
        </form>
      </div>

      {/* My tickets */}
      {tickets.length > 0 && (
        <div className="card" data-testid="my-tickets">
          <h2 className="font-heading uppercase text-2xl flex items-center gap-2">
            <ChatCircleDots size={22} weight="duotone" /> My tickets
          </h2>
          <div className="mt-4 divide-y divide-black/10">
            {tickets.map((t) => (
              <TicketRow key={t.id} ticket={t} onChange={loadTickets} />
            ))}
          </div>
        </div>
      )}

      {/* Roadmap teaser */}
      <div className="card-yellow" data-testid="roadmap-teaser">
        <h2 className="font-heading uppercase text-2xl">Wish forecastly did something else?</h2>
        <p className="mt-2 text-sm text-black/75">
          Vote on what's next, or suggest a feature. We build with our community — the top-voted
          items get prioritised.
        </p>
        <Link to="/roadmap" className="btn-primary mt-4 inline-flex" data-testid="roadmap-link">
          See the roadmap →
        </Link>
      </div>
    </div>
  );
}
